import { isAccountInactiveError, isAuthenticationError } from "@/lib/api-errors";

export const ACCOUNT_INACTIVE_PATH = "/account-inactive";

const AUTH_REDIRECT_BLOCKLIST = new Set([
  "/login",
  "/unauthorized",
  "/forbidden",
  ACCOUNT_INACTIVE_PATH,
  "/maintenance",
  "/maintenance-restricted",
]);

// Once an account-inactive redirect starts, in-flight requests fail 401 (the
// backend clears the session cookie on the first 403 account_inactive) and
// must not race the navigation into the session-expired login flow.
let accountInactiveRedirectStarted = false;

export function redirectToAccountInactive(): void {
  if (typeof window === "undefined") {
    return;
  }
  accountInactiveRedirectStarted = true;
  if (window.location.pathname === ACCOUNT_INACTIVE_PATH) {
    return;
  }
  window.location.replace(ACCOUNT_INACTIVE_PATH);
}

function resolveReturnTo(override?: string): string | null {
  if (typeof window === "undefined") {
    return override ?? null;
  }

  if (override && override.trim()) {
    return override.trim();
  }

  const { pathname, search } = window.location;
  return `${pathname}${search}`;
}

export function buildSessionExpiredLoginPath(overrideReturnTo?: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (accountInactiveRedirectStarted) {
    return null;
  }

  const { pathname } = window.location;
  if (AUTH_REDIRECT_BLOCKLIST.has(pathname)) {
    return null;
  }

  const returnTo = resolveReturnTo(overrideReturnTo);
  const params = new URLSearchParams();
  params.set("reason", "session_expired");
  if (returnTo) {
    params.set("returnTo", returnTo);
  }

  return `/login?${params.toString()}`;
}

/**
 * Hard-redirects for auth-state errors (401 session expiry, 403 account_inactive).
 * Every request path calls this, because the backend clears the session cookie
 * on those responses and component-level handling would race follow-up 401s.
 */
export function redirectForAuthStateError(error: unknown): void {
  if (isAccountInactiveError(error)) {
    redirectToAccountInactive();
  } else if (isAuthenticationError(error)) {
    redirectToSessionExpiredLogin();
  }
}

export function redirectToSessionExpiredLogin(overrideReturnTo?: string): void {
  if (typeof window === "undefined") {
    return;
  }

  const path = buildSessionExpiredLoginPath(overrideReturnTo);
  if (!path) {
    return;
  }

  window.location.assign(path);
}
